import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './contexts/AuthContext.tsx';
import { Toaster } from './components/ui/sonner';

// Google Client ID - REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

// Pages
import Landing from './pages/Landing.tsx';
import Login from './pages/Login.tsx';
import Register from './pages/Register.tsx';
import Discover from './pages/Discover.tsx';
import EventDetail from './pages/EventDetail.tsx';
import MyTickets from './pages/MyTickets.tsx';
import Vibe from './pages/Vibe.tsx';
import Chat from './pages/Chat.tsx';
import EventChatDetail from './pages/EventChatDetail.tsx';
import Profile from './pages/Profile.tsx';
import Memories from './pages/Memories.tsx';
import Friends from './pages/Friends.tsx';
import CreateEvent from './pages/CreateEvent.tsx';
import EditEvent from './pages/EditEvent.tsx';
import ManageEvents from './pages/ManageEvents.tsx';
import TicketScanner from './pages/TicketScanner.tsx';
import HostAnalytics from './pages/HostAnalytics.tsx';
import VirtualRoom from './pages/VirtualRoom.tsx';
import VenueMeetups from './pages/VenueMeetups.tsx';
import LiveStream from './pages/LiveStream.tsx';
import BecomeHost from './pages/BecomeHost.tsx';
import BecomeArtist from './pages/BecomeArtist.tsx';
import ArtistDashboard from './pages/ArtistDashboard.tsx';
import AdminDashboard from './pages/AdminDashboard.tsx';
import MainLayout from './components/MainLayout.tsx';
import AuthCallback from './pages/AuthCallback.tsx';
import ForgotPassword from './pages/ForgotPassword.tsx';
import PaymentSuccess from './pages/PaymentSuccess.tsx';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return user ? <>{children}</> : <Navigate to="/login" replace />;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return !user ? <>{children}</> : <Navigate to="/discover" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/auth/google/callback" element={<AuthCallback />} />
      
      {/* Public routes - accessible without login */}
      <Route element={<MainLayout />}>
        <Route path="/discover" element={<Discover />} />
        <Route path="/events" element={<Discover />} />
        <Route path="/event/:id" element={<EventDetail />} />
      </Route>
      
      {/* Protected routes - require login */}
      <Route element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
        <Route path="/tickets" element={<MyTickets />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/vibe" element={<Vibe />} />
        <Route path="/chat" element={<Chat />} />
        <Route path="/chat/:eventId" element={<EventChatDetail />} />
        <Route path="/memories" element={<Memories />} />
        <Route path="/friends" element={<Friends />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/create-event" element={<CreateEvent />} />
        <Route path="/edit-event/:eventId" element={<EditEvent />} />
        <Route path="/manage-events" element={<ManageEvents />} />
        <Route path="/ticket-scanner/:eventId" element={<TicketScanner />} />
        <Route path="/host-analytics/:eventId" element={<HostAnalytics />} />
        <Route path="/virtual-room/:eventId" element={<VirtualRoom />} />
        <Route path="/venue-meetups/:eventId" element={<VenueMeetups />} />
        <Route path="/live-stream/:eventId" element={<LiveStream />} />
        <Route path="/become-host" element={<BecomeHost />} />
        <Route path="/become-artist" element={<BecomeArtist />} />
        <Route path="/artist-dashboard" element={<ArtistDashboard />} />
        <Route path="/admin" element={<AdminDashboard />} />
      </Route>
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}

export default App;
